import { useQuery, useQueryClient } from "@tanstack/react-query";
import { FC, useEffect, useState } from "react";
import styled, { css } from "styled-components";
import { useParams } from "react-router-dom";

import { CloseButton, StyledDialog } from "@/components/AlertDialogs/Dialog";
import Button from "@/components/Button";
import { Modal } from "@/components/Modal";
import PulsingLoader from "@/components/PulsingLoader";
import { SidePanelFooter } from "@/components/SidePanel";
import Text from "@/components/Text";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import { scrollBarStyles } from "@/utils/theme";
import useToasts from "@/hooks/useToasts";

const Dialog = styled(StyledDialog)`
  width: 900px;
  padding: 0;
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  border-radius: 4px;
  padding: 24px;
  position: relative;
  box-shadow: 0px 2px 8px rgba(34, 34, 34, 0.06);
`;

const StyledCloseButton = styled(CloseButton)`
  position: static;
`;

const ModalHeaderLeft = styled.div`
  display: flex;
  align-items: center;
`;

const ModalContent = styled.div`
  overflow: auto;
  max-height: calc(86vh - 220px);
  padding: 24px 16px;
  ${scrollBarStyles};
`;

const StyledSidePanelFooter = styled(SidePanelFooter)`
  display: flex;
  justify-content: flex-end;
  gap: 16px;
`;

const LoaderHolder = styled.div`
  padding: 24px 0 48px;
`;

const ImagesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(5, 1fr);
  grid-gap: 20px;
`;

const ImageHolder = styled.div`
  width: 100%;
  height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Image = styled.img<{ $selected: boolean }>`
  padding: 3px;
  display: block;
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  border-radius: 4px;
  border: solid 2px transparent;
  cursor: pointer;
  transition: border-color ease-out 0.2s;
  &:hover {
    border-color: ${({ theme }) => theme.colors.purple};
  }
  ${({ $selected }) =>
    $selected &&
    css`
      border-color: ${({ theme }) => theme.colors.purple};
    `}
`;

const NoResultsText = styled(Text)`
  padding: 100px 0;
`;

interface Props {
  onClose: () => void;
  asin: string;
  productId: number;
}

const ProductImagesModal: FC<Props> = ({ onClose, asin, productId }) => {
  const [selectedImage, setSelectedImage] = useState("");

  const alerts = useAlerts();

  const { id } = useParams();

  const toasts = useToasts();

  const [isPosting, setIsPosting] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery(
    ["amazon_product_images", { asin }],
    () =>
      api.get<never, { images: string[] }>(`/v1/amazon-product/images`, {
        params: {
          asin,
        },
      }),
    {
      staleTime: 1000 * 120,
    }
  );

  const onSubmitSelectedImage = async () => {
    const toastId = toasts.loading();

    setIsPosting(true);
    try {
      await api.put(`/v1/amazon-products/${productId}`, {
        selectedImage,
      });

      await queryClient.invalidateQueries(["amazon_products", { id }], {
        exact: false,
      });

      toasts.success(toastId);

      onClose();
    } catch {
      toasts.error(toastId);
    }
    setIsPosting(false);
  };

  useEffect(() => {
    if (isError) {
      alerts.error();
      onClose();
    }
  }, [isError, alerts, onClose]);

  return (
    <Modal zIndex={1}>
      <Dialog
        initial={{ scale: 0.5 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.5 }}
      >
        <ModalHeader>
          <ModalHeaderLeft>
            <Text $type="button">Choose new Image for Product</Text>
          </ModalHeaderLeft>
          <StyledCloseButton onClick={onClose}>
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M13 1L1 13"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M13 13L1 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </StyledCloseButton>
        </ModalHeader>
        <ModalContent>
          {isLoading ? (
            <LoaderHolder>
              <PulsingLoader />
              <Text $color="gray" $type="captionSemibold">
                Please be patient, we are getting your images
              </Text>
            </LoaderHolder>
          ) : (
            <>
              {!!data?.images?.length ? (
                <ImagesGrid>
                  {data.images.map((image, index) => (
                    <ImageHolder key={index}>
                      <Image
                        $selected={selectedImage === image}
                        src={image}
                        onClick={() =>
                          selectedImage === image
                            ? setSelectedImage("")
                            : setSelectedImage(image)
                        }
                      />
                    </ImageHolder>
                  ))}
                </ImagesGrid>
              ) : (
                <NoResultsText>No images found</NoResultsText>
              )}
            </>
          )}
        </ModalContent>
        <StyledSidePanelFooter>
          <Button $type="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={onSubmitSelectedImage}
            disabled={isLoading || !selectedImage || isPosting}
          >
            Confirm
          </Button>
        </StyledSidePanelFooter>
      </Dialog>
    </Modal>
  );
};

export default ProductImagesModal;
